import { useCallback, useEffect, useRef, useState } from 'react';
import { notificationsApi, type PushSubscriptionPayload } from './api';

/* Подписка админки на Web Push: разрешение браузера, подписка PushManager и её копия на сервере,
   по которой бэкенд шлёт уведомления (сообщения в чате, проблемные заказы, заявки на долив).
   Показывает уведомления сервис-воркер (public/sw.js).

   Колокольчик не доверяет тому, что запомнил. Состояние каждый раз собирается заново из трёх
   источников — разрешения браузера, подписки браузера и сервера: при открытии вкладки, при смене
   разрешения в браузере, при возврате во вкладку, раз в RESYNC_INTERVAL_MS и после каждого нажатия.

   Почему. 02.10.2026 у диспетчера колокольчик после отказа браузера стал перечёркнутым, а когда
   уведомления разрешили в настройках сайта, так и остался перечёркнутым и неживым: разрешение
   читалось один раз за вкладку, и помогало только закрыть её. В тот же день на бою не нашлось ни
   одной подписки: «включено» колокольчик показывал по подписке браузера, о которой сервер мог и не
   знать, и уведомление о сообщении в чате не уходило никому.

   Состояния:
     unsupported — браузер не умеет Web Push или сервер не отдал ключ (при сверке спросим снова);
     denied      — уведомления запрещены в браузере; нажатие перепроверяет и подсказывает, где
                   разрешить;
     off / on    — подписки нет / подписка есть и сервер её принял;
     busy        — ждём ответа на запрос разрешения или выключаем. */

export type PushState = 'unsupported' | 'denied' | 'off' | 'on' | 'busy';

/** Чем кончилось нажатие «включить» — по этому колокольчик решает, что подсказать. */
export type EnableResult = 'on' | 'refused' | 'blocked' | 'failed';

/** Как часто сверяться, пока вкладка открыта, даже если ничего не происходило. Не чаще этого же
    сервер слышит подтверждение подписки. */
export const RESYNC_INTERVAL_MS = 5 * 60_000;
/** Сколько ждём один шаг браузера или сервера. Дольше — бросаем: колокольчик не крутится вечно. */
export const STEP_TIMEOUT_MS = 15_000;
/** Ответа на запрос разрешения ждём дольше: человек может раздумывать над окном браузера. */
export const PROMPT_TIMEOUT_MS = 60_000;
/** Что диспетчер выбрал сам: 'on' — включал, 'off' — выключал. Без его выбора подписку сами не
    заводим, а выключенное им — не включаем. */
export const CHOICE_KEY = 'tt-admin-push-choice';

/* There is deliberately NO build-time fallback key. One bundle is deployed to both
   environments, but each backend holds its own VAPID pair (dev and prod differ — verified
   2026-08-11), so any key baked in here is guaranteed wrong for at least one of them. A
   subscription created with the wrong applicationServerKey is accepted by the browser and
   then silently never delivers: the dispatcher sees an enabled bell and no notifications.
   Failing closed — hiding the bell when the backend can't be reached — is the honest
   outcome, and it recovers on its own as soon as /v1/admin/push/config answers again. */

/** Whether the browser has the APIs required for Web Push, independent of any
    key. Exported for unit tests. */
export function browserSupportsPush(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** Convert a base64url VAPID key into the byte array PushManager expects.
    Exported for unit tests. */
export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    bytes[i] = raw.charCodeAt(i);
  }
  return bytes;
}

/** Подписка сделана под этим ключом сервера? Браузер, не сообщающий ключ, считаем совпавшим —
    иначе снимали бы и заводили подписку на каждой сверке. */
export function subscribedWithKey(subscription: PushSubscription, vapidKey: string): boolean {
  const current = subscription.options?.applicationServerKey;
  if (!current) return true;
  const actual = new Uint8Array(current);
  const expected = urlBase64ToUint8Array(vapidKey);
  return actual.length === expected.length && actual.every((byte, i) => byte === expected[i]);
}

/** Шаг браузера или сервера, но не дольше `ms`: зависший шаг не должен держать колокольчик. */
function withTimeout<T>(promise: Promise<T>, ms = STEP_TIMEOUT_MS): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error('timeout')), ms);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        window.clearTimeout(timer);
        reject(error instanceof Error ? error : new Error(String(error)));
      },
    );
  });
}

function readChoice(): 'on' | 'off' | null {
  try {
    const value = localStorage.getItem(CHOICE_KEY);
    return value === 'on' || value === 'off' ? value : null;
  } catch {
    return null;
  }
}

function writeChoice(choice: 'on' | 'off'): void {
  try {
    localStorage.setItem(CHOICE_KEY, choice);
  } catch {
    // Хранилище недоступно — выбор не запомнится, и потерянную подписку колокольчик сам не
    // восстановит; включить его по-прежнему можно нажатием.
  }
}

function toPayload(subscription: PushSubscription): PushSubscriptionPayload {
  const json = subscription.toJSON();
  return {
    endpoint: json.endpoint ?? subscription.endpoint,
    p256dh: json.keys?.p256dh ?? '',
    auth: json.keys?.auth ?? '',
  };
}

/** Воркер с активной регистрацией. В проде его регистрирует main.tsx; здесь — на случай
    dev-сборки и вкладки, открытой раньше, чем он успел. */
async function activeRegistration(): Promise<ServiceWorkerRegistration> {
  await withTimeout(navigator.serviceWorker.register('/sw.js'));
  return withTimeout(navigator.serviceWorker.ready);
}

export function usePushNotifications(): {
  state: PushState;
  enable: () => Promise<EnableResult>;
  disable: () => Promise<void>;
  recheck: () => Promise<PushState>;
} {
  const [state, setState] = useState<PushState>('busy');
  // Ключ сервера: только от него самого (см. выше). Нет ответа — спросим на следующей сверке.
  const vapidKey = useRef<string | null>(null);
  // Какую подписку сервер последней принял и когда — подтверждаем не чаще RESYNC_INTERVAL_MS.
  const confirmed = useRef<{ endpoint: string; at: number } | null>(null);
  // Сверка, включение и выключение идут строго по одному и не перебивают друг друга.
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  // Сверка, уже стоящая в очереди: новые поводы (фокус, смена вкладки) к ней присоединяются.
  const queuedSync = useRef<Promise<PushState> | null>(null);
  // Пока человек отвечает на окно браузера или идёт выключение, фоновые сверки не перерисовывают
  // колокольчик: его состояние решит сверка по итогам нажатия.
  const acting = useRef(0);
  const alive = useRef(true);

  const serial = useCallback(<T>(task: () => Promise<T>): Promise<T> => {
    const run = queue.current.then(task);
    queue.current = run.catch(() => undefined);
    return run;
  }, []);

  /** Сверка с правдой. Возвращает, что показывать; бросает, если браузер или сервер не ответили. */
  const reconcile = useCallback(async (): Promise<PushState> => {
    if (!browserSupportsPush()) return 'unsupported';
    if (!vapidKey.current) {
      // Сервер не ответил (например, перезапускался при выкладке) — колокольчик скрыт до
      // следующей сверки, а не до перезагрузки вкладки, как было.
      try {
        const config = await withTimeout(notificationsApi.config());
        vapidKey.current = config.enabled ? config.vapid_public_key : null;
      } catch {
        vapidKey.current = null;
      }
      if (!vapidKey.current) return 'unsupported';
    }
    const key = vapidKey.current;
    const permission = Notification.permission;
    if (permission === 'denied') return 'denied';

    let registration = (await withTimeout(navigator.serviceWorker.getRegistration())) ?? null;
    let subscription = registration
      ? await withTimeout(registration.pushManager.getSubscription())
      : null;
    const choice = readChoice();

    if (subscription && (choice === 'off' || !subscribedWithKey(subscription, key))) {
      // Выключено диспетчером, а подписка осталась (отписка не дошла), или подписка под старым
      // ключом сервера, по которой служба пушей ничего не примет. Снимаем — первую насовсем,
      // вторую заведём ниже заново.
      await withTimeout(notificationsApi.unsubscribe(subscription.endpoint)).catch(() => undefined);
      await withTimeout(subscription.unsubscribe()).catch(() => false);
      subscription = null;
      confirmed.current = null;
    }
    if (permission !== 'granted') return 'off';
    if (!subscription) {
      // Разрешение есть, подписки нет: истекла, снята браузером, сменился ключ. Заводим сами, но
      // только если уведомления диспетчер включал.
      if (choice !== 'on') return 'off';
      registration = await activeRegistration();
      subscription = await withTimeout(
        registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(key),
        }),
      );
    } else if (choice === null) {
      // Подписку заводили раньше, чем колокольчик стал запоминать выбор, — значит, включали.
      writeChoice('on');
    }

    // Сервер должен знать эту подписку, иначе «включено» — неправда: пуши идут только по его копии.
    const payload = toPayload(subscription);
    const last = confirmed.current;
    if (!last || last.endpoint !== payload.endpoint || Date.now() - last.at >= RESYNC_INTERVAL_MS) {
      await withTimeout(notificationsApi.subscribe(payload));
      confirmed.current = { endpoint: payload.endpoint, at: Date.now() };
    }
    return 'on';
  }, []);

  const sync = useCallback((): Promise<PushState> => {
    if (queuedSync.current) return queuedSync.current;
    const run = serial(async () => {
      // Сверка началась: поводы, пришедшие во время неё, поставят следующую.
      queuedSync.current = null;
      let next: PushState;
      try {
        next = await reconcile();
      } catch {
        // Браузер или сервер не ответили вовремя. «Включено» без подтверждения не показываем;
        // следующая сверка попробует снова.
        next = browserSupportsPush() && Notification.permission === 'denied' ? 'denied' : 'off';
      }
      if (alive.current && acting.current === 0) setState(next);
      return next;
    });
    queuedSync.current = run;
    return run;
  }, [reconcile, serial]);

  useEffect(() => {
    alive.current = true;
    void sync();

    const onVisible = () => {
      if (document.visibilityState === 'visible') void sync();
    };
    const onFocus = () => void sync();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    const timer = window.setInterval(() => void sync(), RESYNC_INTERVAL_MS);

    // Разрешение поменяли в браузере (значок слева от адреса → «Уведомления») — сверяемся сразу.
    // Разрешили сами — значит, уведомления нужны: подписка заводится без второго нажатия.
    let status: PermissionStatus | null = null;
    const onPermission = () => {
      if (status?.state === 'granted') writeChoice('on');
      void sync();
    };
    if (typeof navigator !== 'undefined' && navigator.permissions) {
      navigator.permissions
        .query({ name: 'notifications' })
        .then((current) => {
          if (!alive.current) return;
          status = current;
          current.addEventListener('change', onPermission);
        })
        .catch(() => {
          // Браузер не следит за этим разрешением — остаются фокус, возврат во вкладку и таймер.
        });
    }

    return () => {
      alive.current = false;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      window.clearInterval(timer);
      status?.removeEventListener('change', onPermission);
    };
  }, [sync]);

  const enable = useCallback(async (): Promise<EnableResult> => {
    if (!browserSupportsPush()) return 'failed';
    acting.current += 1;
    setState('busy');
    let answer: NotificationPermission;
    try {
      // Прямо в нажатии, до любого ожидания: без жеста пользователя браузер окно не покажет.
      answer = await withTimeout(Notification.requestPermission(), PROMPT_TIMEOUT_MS);
    } catch {
      answer = Notification.permission;
    } finally {
      acting.current -= 1;
    }
    if (answer === 'granted') writeChoice('on');
    const next = await sync();
    if (next === 'on') return 'on';
    if (next === 'denied') return 'blocked';
    // Браузер сказал «нет», но не запретил (закрыли окно, браузер сам приглушил запрос) — кнопка
    // остаётся живой, можно нажать ещё раз.
    return answer === 'granted' ? 'failed' : 'refused';
  }, [sync]);

  const disable = useCallback(async (): Promise<void> => {
    if (!browserSupportsPush()) return;
    writeChoice('off');
    acting.current += 1;
    setState('busy');
    try {
      await serial(async () => {
        const registration = await withTimeout(navigator.serviceWorker.getRegistration());
        const subscription = registration
          ? await withTimeout(registration.pushManager.getSubscription())
          : null;
        if (subscription) {
          await withTimeout(notificationsApi.unsubscribe(subscription.endpoint)).catch(
            () => undefined,
          );
          await withTimeout(subscription.unsubscribe());
        }
        confirmed.current = null;
      });
    } catch {
      // Отписка в браузере не дошла — сверка ниже доведёт её до конца: выбор «выключено» записан.
    } finally {
      acting.current -= 1;
    }
    await sync();
  }, [serial, sync]);

  return { state, enable, disable, recheck: sync };
}
